import React, { FC, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentMessage,
  AgentStreamEvents,
  UserMessageEvent,
  threadChannel,
} from '@dreamai/types';
import { usePusher } from '../../../contexts/PusherContext';
import { PUSHER_ENV } from '../../../lib/pusherClient';

interface ThirdPartyMessageHandlerProps {
  threadId: string | null;
  setMessages: (
    messages:
      | AgentMessage[]
      | ((prevMessages: AgentMessage[]) => AgentMessage[]),
  ) => void;
}

/**
 * Component that listens for user messages from all sources (web app, WhatsApp, phone)
 * and adds them to the message list for real-time collaboration
 */
const ThirdPartyMessageHandler: FC<ThirdPartyMessageHandlerProps> = ({
  threadId,
  setMessages,
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const currentUserId = user?.sub;
  // usePusher() returns the channel by default (or null when the provider /
  // connection hasn't initialised yet). Use the `full` form to get the
  // pusherInstance, which is what we actually need for subscribe/unsubscribe.
  const pusherCtx = usePusher(true);
  const pusher = pusherCtx?.pusherInstance ?? null;

  const handleUserMessage = useCallback(
    (data: UserMessageEvent) => {
      console.log('Received user message:', data);

      // Filter out messages from the current user to avoid duplicates
      // (we already show optimistic messages when you send)
      if (data.userId === currentUserId) {
        console.log('Skipping own message to avoid duplicate');
        return;
      }

      // Create a new user message with full metadata
      const newMessage: AgentMessage = {
        role: 'user',
        content: data.message,
        timestamp: data.timestamp,
        metadata: {
          userId: data.userId,
          userName: data.userName,
        },
      };

      // Add the message to the list
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    },
    [setMessages, currentUserId],
  );

  useEffect(() => {
    if (!threadId || !pusher) return;

    // Subscribe to the thread's channel
    const channelName = threadChannel(PUSHER_ENV, threadId);
    const channel = pusher.subscribe(channelName);

    // Listen for user message events
    channel.bind(AgentStreamEvents.USER_MESSAGE, handleUserMessage);

    return () => {
      // Unbind event and unsubscribe from channel when component unmounts
      channel.unbind(AgentStreamEvents.USER_MESSAGE);
      pusher.unsubscribe(channelName);
    };
  }, [threadId, pusher, handleUserMessage, getAccessTokenSilently]);

  // This component doesn't render anything
  return null;
};

export default ThirdPartyMessageHandler;
